import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/axios';

const PHONE_REGEX = /^[0-9+\-\s()]{7,20}$/;

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

// Reached only via the Google button's needsOnboarding branch (see useGoogleAuthFlow) —
// the idToken/email/fullName come from router state, verified server-side, never the URL.
function GoogleOnboardingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { t } = useTranslation(['googleOnboarding', 'common']);

  const { idToken, email, fullName, plan } = location.state || {};

  useEffect(() => {
    if (!idToken || !email) {
      navigate('/login', { replace: true });
    }
  }, [idToken, email, navigate]);

  const [form, setForm] = useState({ restaurantName: '', phone: '', terms: false });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!idToken || !email) return null;

  const getErrors = (f) => {
    const errs = {};
    if (!f.restaurantName.trim() || f.restaurantName.trim().length < 2)
      errs.restaurantName = t('errors.restaurantNameRequired');
    if (!f.phone.trim())
      errs.phone = t('errors.phoneRequired');
    else if (!PHONE_REGEX.test(f.phone.trim()))
      errs.phone = t('errors.phoneInvalid');
    if (!f.terms)
      errs.terms = t('errors.termsRequired');
    return errs;
  };

  const errors = getErrors(form);
  const isFormValid = Object.keys(errors).length === 0;
  const visibleError = (field) => (touched[field] ? errors[field] : '');

  const inputClass = (field) =>
    `w-full px-3.5 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 ${
      visibleError(field) ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-orange-500'
    }`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSubmitError('');
  };

  const handleBlur = (e) => setTouched((prev) => ({ ...prev, [e.target.name]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ restaurantName: true, phone: true, terms: true });
    if (!isFormValid) return;

    setLoading(true);
    setSubmitError('');
    try {
      const payload = {
        idToken,
        restaurantName: form.restaurantName.trim(),
        phoneNumber: form.phone.trim(),
        termsAccepted: form.terms,
        ...(plan ? { subscribedPlan: plan } : {}),
      };
      const res = await apiClient.post('/api/auth/google/complete-signup', payload);
      auth.login(res.data.token);
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      setSubmitError(data?.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight">
            <span style={{ color: '#085041' }}>Bento</span>
            <span className="text-orange-600">Ping</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">{t('common:tagline')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-orange-100/60 border border-orange-100 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('title')}</h2>
              <p className="text-sm text-gray-500">
                {t('continueAs', { name: fullName || email, email })}
              </p>
            </div>
            <LanguageSwitcher className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-gray-50 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200 cursor-pointer" />
          </div>

          {submitError && (
            <div role="alert" className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              </svg>
              <span>{submitError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label htmlFor="restaurantName" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('restaurantName')}
              </label>
              <input
                id="restaurantName"
                name="restaurantName"
                type="text"
                value={form.restaurantName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="My Restaurant"
                disabled={loading}
                className={inputClass('restaurantName')}
              />
              {visibleError('restaurantName') && <p className="mt-1.5 text-xs text-red-600">{visibleError('restaurantName')}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('phoneNumber')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="+91 98765 43210"
                disabled={loading}
                className={inputClass('phone')}
              />
              {visibleError('phone') && <p className="mt-1.5 text-xs text-red-600">{visibleError('phone')}</p>}
            </div>

            <div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  name="terms"
                  type="checkbox"
                  checked={form.terms}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-orange-500 disabled:opacity-60"
                />
                <span className="text-sm text-gray-700">
                  {t('termsLabel')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">
                    {t('termsLink')}
                  </a>
                  {' '}{t('termsAnd')}{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">
                    {t('privacyLink')}
                  </a>
                </span>
              </label>
              {visibleError('terms') && <p className="mt-1.5 text-xs text-red-600">{visibleError('terms')}</p>}
            </div>

            <button
              type="submit"
              disabled={loading || !isFormValid}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (<><Spinner />{t('submitting')}</>) : t('submit')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            <Link to="/login" className="font-medium text-orange-600 hover:text-orange-700 hover:underline">
              {t('cancel')}
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          {t('common:footer', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}

export default GoogleOnboardingPage;
